import moment from 'moment';
import momentTimezone from 'moment-timezone';
import { config } from '@config/pendo.config.json';
import { RailsRoutes } from 'shared/services/rails-routes';
import { camelCase } from 'lodash';

import {
  IntegrationTypes,
  SessionTypes,
} from 'redux/schemas/models/live-event';
import {
  AuthenticationStatus,
  IntegrationAuth,
  OauthIntegration,
} from 'redux/schemas/models/mentoring-program-sessions';

const DATE_FORMAT = 'MMMM D';

const SESSION_TYPES_FOR_MENTORSHIP_PROGRAM = [
  SessionTypes.ZOOM,
  SessionTypes.MS_TEAMS,
];

export const getCadenceWarningModal = ({
  translations,
  onCancel,
  onConfirm,
  sessions: items,
}) => {
  const pendoTags = config.pendo.athena.mentorshipProgram.participantHome.sessions.planSessionFlyout.cadenceWarningModal;

  const sessions = items.map(({ id, title, scheduledDate }) => (
    <div key={id} className='session'>
      <div className='session-title'>{title} - {moment(scheduledDate).format(DATE_FORMAT)}</div>
    </div>
  ));

  const bodyText = (
    <div>
      {translations.DESCRIPTION_ABOVE()}
      <div className='sessions-container my-4'>{sessions}</div>
      {translations.DESCRIPTION_BELOW()}
    </div>
  );

  return {
    title: translations.TITLE(),
    bodyText,
    confirmText: translations.CONFIRM(),
    cancelText: translations.CANCEL(),
    cancelDataQa: pendoTags.cancel,
    confirmDataQa: pendoTags.confirm,
    cancelPendoTag: pendoTags.cancel,
    confirmPendoTag: pendoTags.confirm,
    onCancel,
    onConfirm,
  };
};

const integrationKeyFromSessionType = {
  [SessionTypes.ZOOM]: 'zoomOauth',
  [SessionTypes.MS_TEAMS]: 'microsoftGraphOauth',
};

const sessionTypeFromIntegrationKey = {
  [IntegrationTypes.ZOOM_USER_LEVEL]: SessionTypes.ZOOM,
  [IntegrationTypes.MS_TEAMS_USER_LEVEL]: SessionTypes.MS_TEAMS,
};

export const hasCreatedLiveSessions = (integrations: IntegrationAuth, sessionType: SessionTypes) => {
  const type = integrationKeyFromSessionType[sessionType];
  const integration = integrations[type];
  const {
    hasCreatedLiveSessions: hasCreated = false,
  } = integration?.createdLiveSessions || {};

  return hasCreated;
};

export const isIntegrationAuthenticated = (integrations: IntegrationAuth, sessionType: SessionTypes) => {
  const type = integrationKeyFromSessionType[sessionType];
  const authenticatedAccount = integrations[type]?.authenticatedStatus === AuthenticationStatus.AUTHENTICATED;

  return authenticatedAccount;
};

export const getAuthenticatedAccount = (integrations: IntegrationAuth) => (integrations ? (
  SESSION_TYPES_FOR_MENTORSHIP_PROGRAM.find(
    (type) => isIntegrationAuthenticated(integrations, type),
  )
) : null);

export const getAuthenticatedAccounts = (integrations: IntegrationAuth) => (integrations ? (
  SESSION_TYPES_FOR_MENTORSHIP_PROGRAM.filter(
    (type) => isIntegrationAuthenticated(integrations, type),
  )
) : []);

const isIntegrationTokenExpired = (integration: OauthIntegration) => (
  integration?.authenticatedStatus === AuthenticationStatus.NOT_AUTHENTICATED
);

export type CalendarIntegrationPayload = {
  id: number;
  userId: number;
  provider: string;
  email: string;
  scope: string;
  authenticatedAt: string;
  expiresAt: string;
};

export type ExpiredAccount = {
  sessionType: SessionTypes;
  email: string;
};

export const isTokenExpired = (
  integrations: IntegrationAuth,
  calendarIntegration?: CalendarIntegrationPayload,
): ExpiredAccount[] => {
  const { source = [] } = integrations || {};
  const expiredIntegrations = [];
  source?.forEach((type) => {
    const integrationType = camelCase(type);
    const integration: OauthIntegration = integrations[integrationType];
    if (isIntegrationTokenExpired(integration)) {
      expiredIntegrations.push({
        sessionType: sessionTypeFromIntegrationKey[type],
        email: integration?.authenticationEmail,
      });
    }
  });

  if (calendarIntegration) {
    const expiresAt = moment(calendarIntegration.expiresAt);
    if (moment().isAfter(expiresAt)) {
      expiredIntegrations.push({
        sessionType: SessionTypes.MS_CALENDAR,
        email: calendarIntegration.email,
      });
    }
  }

  return expiredIntegrations;
};

export const isAccountNotConfigured = (integrations: IntegrationAuth): ExpiredAccount[] => {
  const { source = [] } = integrations || {};
  const notConfiguredIntegration = [];
  source?.forEach((type) => {
    const integrationType = camelCase(type);
    const integration = integrations[integrationType];
    if (integration?.authenticatedStatus === AuthenticationStatus.NOT_CONFIGURED) {
      notConfiguredIntegration.push({
        sessionType: sessionTypeFromIntegrationKey[type],
        email: integration?.authenticatedUser?.email,
      });
    }
  });

  return notConfiguredIntegration;
};

export const getSessionType = (source: IntegrationTypes[]) => {
  const integration = source[0];
  return sessionTypeFromIntegrationKey[integration] || integration;
};

export const formatSessionTime = (dateString, duration, timeZone) => {
  const { ianaTimeZone, timeZoneAbbreviation } = timeZone;
  try {
    const startTime = momentTimezone.tz(dateString, ianaTimeZone);
    const endTime = duration ? startTime.clone().add(duration, 'minutes') : null;
    const formattedStartTime = startTime.format('ddd, MMMM D • h:mm A');
    const formattedEndTime = endTime ? ` - ${endTime.format('h:mm A')}` : '';
    return `${formattedStartTime}${formattedEndTime} ${timeZoneAbbreviation}`;
  } catch (error) {
    return '';
  }
};

const routeMap = {
  [SessionTypes.ZOOM]: 'zoomOAuthInstallationUrlForMentoringProgram',
  [SessionTypes.MS_TEAMS]: 'msTeamOAuthInstallationUrlForMentoringProgram',
  [SessionTypes.MS_CALENDAR]: 'microsoftCalendarOAuthInstallationUrlForMentoringProgram',
};

type GetOauthLinkParams = {
  sessionType: SessionTypes;
  mentoringProgramId: number;
  enrollmentId: number;
  refererUrl: string;
};

export const getOAuthLink = (params: GetOauthLinkParams) => {
  const {
    sessionType,
    mentoringProgramId,
    enrollmentId,
    refererUrl,
  } = params;
  const route = routeMap[sessionType];
  const href = RailsRoutes[route](mentoringProgramId, enrollmentId, refererUrl);

  return href;
};
